import React, { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { UploadCloud, FileSpreadsheet, Loader2, ArrowRight, Check, Pill, Receipt, Package, ArrowLeft } from "lucide-react";

const STEPS = [
  { key: "inventario", label: "Inventario", icon: Package, required: true, desc: "stock, PVP, PMC, familia, min/max" },
  { key: "venta_libre", label: "Venta Libre", icon: Receipt, required: false, desc: "ventas mostrador (sin receta)" },
  { key: "venta_receta", label: "Venta Receta", icon: Pill, required: false, desc: "ventas con receta médica" },
];

function FileDrop({ file, onPick, label, icon: Icon, desc, testid }) {
  const inputRef = useRef(null);
  return (
    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-500 transition cursor-pointer bg-white"
         onClick={() => inputRef.current?.click()} data-testid={testid}>
      <input ref={inputRef} type="file" accept=".xlsx,.xls,.xlsm" className="hidden"
             onChange={(e) => onPick(e.target.files?.[0] || null)} data-testid={`${testid}-input`} />
      <div className="flex items-center gap-3">
        <div className={"h-10 w-10 rounded-lg flex items-center justify-center " + (file ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500")}>
          {file ? <Check size={18} /> : <Icon size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{label}</div>
          {file ? (
            <div className="text-xs text-emerald-700 truncate">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>
          ) : (
            <div className="text-xs text-slate-500">{desc}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function UploadFarmatic() {
  const [name, setName] = useState("");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [files, setFiles] = useState({ inventario: null, venta_libre: null, venta_receta: null });
  const [reclassify, setReclassify] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const periodDays = (() => {
    if (!periodFrom || !periodTo) return null;
    const d1 = new Date(periodFrom), d2 = new Date(periodTo);
    if (isNaN(d1) || isNaN(d2) || d2 < d1) return null;
    return Math.round((d2 - d1) / 86400000) + 1;
  })();

  const canSubmit = name && files.inventario && periodFrom && periodTo && periodDays > 0 &&
                    (files.venta_libre || files.venta_receta);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { setError("Completa todos los campos obligatorios."); return; }
    setLoading(true); setError("");
    const fd = new FormData();
    fd.append("name", name);
    fd.append("period_from", periodFrom);
    fd.append("period_to", periodTo);
    fd.append("inventario", files.inventario);
    if (files.venta_libre) fd.append("venta_libre", files.venta_libre);
    if (files.venta_receta) fd.append("venta_receta", files.venta_receta);
    if (reclassify) fd.append("reclassify_with_botplus", "true");
    try {
      const { data } = await api.post("/studies/upload-farmatic", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      navigate(`/studies/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="p-8 fade-in max-w-4xl" data-testid="upload-farmatic-page">
      <Link to="/upload" className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-3">
        <ArrowLeft size={12} /> Volver a Subir estudio
      </Link>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Farmatic · Importación 3 ficheros</div>
      <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1 mb-2">Subir estudio desde Farmatic</h1>
      <p className="text-slate-600 mb-8 max-w-3xl">
        Sube los <b>3 archivos</b> exportados directamente desde tu programa Farmatic. El inventario es obligatorio.
        Las ventas se sumarán automáticamente por código (libre + receta) sin que tengas que combinar Excels manualmente.
      </p>

      <form onSubmit={submit} className="space-y-5">
        {/* Nombre + fechas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nombre del estudio *</label>
            <input data-testid="farmatic-name" required value={name} onChange={(e) => setName(e.target.value)}
                   className="input mt-1" placeholder="Ej: Farmatic Mayo 2026" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Desde *</label>
            <input data-testid="farmatic-from" required type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)}
                   className="input mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Hasta *</label>
            <input data-testid="farmatic-to" required type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)}
                   className="input mt-1" />
            {periodDays != null && (
              <p className="text-[11px] text-slate-500 mt-1">Periodo: <b>{periodDays} días</b></p>
            )}
          </div>
        </div>

        {/* 3 file dropzones */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {STEPS.map((s) => (
            <FileDrop
              key={s.key}
              file={files[s.key]}
              onPick={(f) => setFiles((prev) => ({ ...prev, [s.key]: f }))}
              label={s.label + (s.required ? " *" : " (opcional)")}
              icon={s.icon}
              desc={s.desc}
              testid={`farmatic-file-${s.key}`}
            />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="reclassify" checked={reclassify} onChange={(e) => setReclassify(e.target.checked)}
                 data-testid="farmatic-reclassify" />
          <label htmlFor="reclassify" className="text-sm text-slate-700">
            Reclasificar parafarmacia con catálogo BOT PLUS
          </label>
        </div>

        {error && (
          <div data-testid="farmatic-error" className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded border border-red-200">{error}</div>
        )}

        <div className="flex items-center gap-3">
          <button type="submit" disabled={loading || !canSubmit} className="btn btn-primary" data-testid="farmatic-submit">
            {loading
              ? <><Loader2 size={16} className="animate-spin" /> Procesando los 3 ficheros…</>
              : <>Crear estudio <ArrowRight size={16} /></>}
          </button>
          {!canSubmit && !loading && (
            <p className="text-xs text-slate-500">Necesitas: nombre, fechas, inventario y al menos un fichero de ventas.</p>
          )}
        </div>
      </form>
    </div>
  );
}
